import { Container } from '../../../Components/Container';
import React , { useState } from 'react';
import { Grid, Link } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import PercentIcon from '@mui/icons-material/Percent';
import { Column, Row } from '../../../Components/Grid';
import { useQuery } from 'react-query';
import { PageLoaderShow } from '../../../Components/PageLoader/Show';
import { typeLabels, statusControl, maturityLevels } from '../../../utils/labels_utils'
import { tableHeader } from './table-header'
import { tableControls } from './table-controls'
import { SummaryCard } from '../../../Components/SummaryCard'
import EntityProgressTable from '../../../Components/EntityProgressTable'
import ExpandableTable from '../../../Components/ExpandableTable';
import StatusStackedBarChart from '../../../Components/StatusStackedBarChart';
import GroupedProgressCard from '../../../Components/GroupedProgressCard';
import DonutChartCard from '../../../Components/DonutChartCard';
import PieChartWithLegend from '../../../Components/PieChartWithLegend';
import ProgressInfoCard from '../../../Components/ProgressInfoCard';
import ChartLine from '../../../Components/LineChart';
import CardBarHorizontal from '../../../Components/CardBarHorizontal';

import { Tabs, Tab, Box } from '@mui/material';
import { Card } from '../../../Components/Card';
import { useSelector } from 'react-redux';
import { useAuthTenant } from '../../../store/auth';
import { getComplianceProgramDashboard } from '../../../services/complice-program';
import { Information } from '../../../Components/Information';
import { PageHelper } from '../../../Components/PageHelper';
import { useTranslation } from 'react-i18next';


export const ComplianceProgramDashboard = () => {
  const { t } = useTranslation(['company']);
  const selectedTenant = useSelector(useAuthTenant);
  const [tabIndex, setTabIndex] = useState(0);

  const {
    isLoading,
    error,
    data: data_dashboard
  } = useQuery('getComplianceProgramDashboard', async () => {
    try {
      const data = await getComplianceProgramDashboard(selectedTenant._id);
      return data.status === 200 ? data.data : null;
    } catch (err) {
      if (err?.response?.status === 404) {
        return { hasComplianceProgram: false };
      }
      throw err;
    }
  });

  if (isLoading) return <p>{t('company:complianceProgram.loading')}</p>;
  if (error) return <p>{t('company:complianceProgram.loadingError')}</p>;

  if (data_dashboard?.hasComplianceProgram === false) {
    return (
      <Information
        title={t('company:complianceProgram.notFound.title')}
        description={
          <>
            <div>{t('company:complianceProgram.notFound.description')}</div>
            <div>
              <Link
                href="/compliance-program/add"
                style={{ color: '#007bff', textDecoration: 'underline' }}
              >
                {t('company:complianceProgram.notFound.link')}
              </Link>
            </div>
          </>
        }
        style={{
          marginTop: '10px',
          marginBottom: '10px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeeba',
          borderRadius: '8px',
          padding: '16px',
          color: '#856404',
        }}
      />
    );
  }

  const entitiesByType = data_dashboard.entitiesByType || {};
  const types = Object.keys(entitiesByType);
  const handleChange = (_event, newIndex) => {
    setTabIndex(newIndex);
  };
  const totalEntities = Object.values(data_dashboard.entityCount).reduce((acc, curr) => acc + curr, 0);
  const entidades = Object.keys(data_dashboard.entityCount).map((key) => ({
    name: typeLabels[key],
    value: data_dashboard.entityCount[key],
    percent: ((data_dashboard.entityCount[key] / totalEntities) * 100).toFixed(2)
  }));

  const total = Object.values(data_dashboard.maturityCounts).reduce((acc, curr) => acc + curr, 0);
  const maturityData = Object.keys(maturityLevels).map((key) => ({
    name: maturityLevels[key],
    value: data_dashboard.maturityCounts[key],
    total: total
  }));

  const entityCountsByTypeWithStatus = data_dashboard.entityCountsByTypeWithStatus;
  const dataEntityCountsByTypeWithStatus = Object.keys(entityCountsByTypeWithStatus).map((key) => ({
    entidade: typeLabels[key],
    ...entityCountsByTypeWithStatus[key]
  }));

  const entityProgress = data_dashboard.entityTypeProcess;
  const dataEntityProgress = Object.keys(entityProgress).map((key) => ({
    ...entityProgress[key],
    label: typeLabels[entityProgress[key].label],
  }));

  const progress = data_dashboard.totalControls > 0 ? (data_dashboard.linkedControls / data_dashboard.totalControls) * 100 : 0;

  const pieChartData = Object.keys(data_dashboard.statusCounts).map((key) => ({
    name: statusControl[key].label,
    value: data_dashboard.statusCounts[key],
    color: statusControl[key].color
  }));

  const link_ = `compliance-program/${data_dashboard.gapAnalysis?._id}`;
  const dataProgressFramewok = data_dashboard.progressoPorFramework.map(item => ({
    label: item.name,
    value: item.progress
  }));

  return (
    <>
      {!isLoading && !error && (
        <Container title={`${data_dashboard.gapAnalysis?.name}`}
                   breadcrumb={`${t('company:complianceProgram.dashboard.breadcrumb')} / ${data_dashboard.gapAnalysis?.name}`}
        >
          <Row md={'3'} className="mt-1 g-1">
            <Column xs={12} md={4} className="p-1" >
              <Grid item xs={12} data-tour="progress-card">
                <ProgressInfoCard
                  title={t('company:complianceProgram.dashboard.progress.title')}
                  description={t('company:complianceProgram.dashboard.progress.description')}
                  icon={    <PercentIcon sx={{ color: "#0039c4", fontSize: 20}} />}
                  total={100}
                  value={data_dashboard.overallProgress }
                  progress={data_dashboard.overallProgress }
                  unitLabel={t('company:complianceProgram.dashboard.progress.unit')}
                />
              </Grid>
              <Grid item xs={12} className="mt-1" data-tour="controls-card">
                <ProgressInfoCard
                  title={t('company:complianceProgram.dashboard.controls.title')}
                  description={t('company:complianceProgram.dashboard.controls.description')}
                  icon={    <SecurityIcon sx={{ color: "#0039c4", fontSize: 20 }} />}
                  total={data_dashboard.totalControls}
                  value={data_dashboard.linkedControls}
                  progress={progress}
                  unitLabel={t('company:complianceProgram.dashboard.controls.unit')}
                />
              </Grid>
            </Column>
            <Column xs={12} md={8} className="p-1">
              <div data-tour="historical-chart">
                <ChartLine title={t('company:complianceProgram.dashboard.historical')} data={data_dashboard.historicalProgress} tooltipLabel={t('company:complianceProgram.dashboard.tooltip')} color="#0039c4" />
              </div>
            </Column>
          </Row>
          <Row className="g-1">
            <Column xs={12} md={4} className="p-1">
              <div data-tour="framework-progress">
                <CardBarHorizontal
                  title={t('company:complianceProgram.dashboard.frameworkProgress')}
                  data={dataProgressFramewok}
                  valueLabel={t('company:complianceProgram.dashboard.tooltip')}
                />
              </div>
            </Column>
            <Column xs={12} md={5} className="p-1">
              <div data-tour="status-chart">
                <PieChartWithLegend title={t('company:complianceProgram.dashboard.statusDistribution')} data={pieChartData} />
              </div>
            </Column>
            <Column xs={12} md={3} className="p-1">
              <div data-tour="maturity-card">
                <GroupedProgressCard title={t('company:complianceProgram.dashboard.maturityLevel')} data={maturityData} />
              </div>
            </Column>
          </Row>
          <Row className="g-1">
            <Column xs={12} md={3} className="p-1"> {/* `p-0` remove padding */}
              <div data-tour="entity-donut">
                <DonutChartCard
                  title={t('company:complianceProgram.dashboard.controlsByEntity')}
                  total={data_dashboard.linkedControls}
                  data={entidades}
                />
              </div>
            </Column>
            <Column xs={12} md={5} className="p-1">
              <div data-tour="status-by-entity">
                <StatusStackedBarChart
                  title={t('company:complianceProgram.dashboard.statusByEntity')}
                  data={dataEntityCountsByTypeWithStatus}
                  categoryKey="entidade"
                />
              </div>
            </Column>
            <Column xs={12} md={4} className="p-1">
              <Grid item data-tour="unique-entities">
                <SummaryCard title={t('company:complianceProgram.dashboard.uniqueEntities')} value={data_dashboard.uniqueEntityCount} />
              </Grid>
              <Grid item className="mt-1" data-tour="entity-progress">
                <EntityProgressTable
                  title={t('company:complianceProgram.dashboard.entityProgress')}
                  data={dataEntityProgress}
                />
              </Grid>
            </Column>
          </Row>
          <Row className="g-1">
            <Card>
              <Tabs value={tabIndex} onChange={handleChange} data-tour="entity-tabs">
                {types.map((type) => (
                  <Tab key={type} label={typeLabels[type]} />
                ))}
              </Tabs>

              <Box mt={2}>
                {types.map((type, index) => (
                  <div key={type} hidden={tabIndex !== index}>
                    {tabIndex === index && (
                      <ExpandableTable
                        rows={entitiesByType[type]}
                        columns={tableHeader(t)}
                        link={link_}
                        linkExtend="compliance-program/control"
                        entityColumns={tableControls(t)}
                        enableSearch={true}
                        enableSorting={true}
                        title={t('company:complianceProgram.dashboard.controlsBy', { type: typeLabels[type] })}
                      />
                    )}
                  </div>
                ))}
              </Box>
            </Card>
          </Row>
          <PageHelper steps={tourSteps} tourKey="compliance-program-dashboard" />
        </Container>
      )}
      {isLoading && <PageLoaderShow />}
      {error && <div>{t('company:complianceProgram.show.error')}</div>}
    </>
  )

}

const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo ao Dashboard do Programa de Privacidade e Proteção de Dados! Este painel apresenta uma visão completa do progresso do seu programa de conformidade.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="progress-card"]',
    content: 'Este card mostra o progresso geral do seu Programa de Privacidade e Proteção de Dados. O percentual indica quanto do programa foi implementado.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="controls-card"]',
    content: 'Aqui é exibido quantos controles foram vinculados às entidades do total disponível no programa.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="historical-chart"]',
    content: 'O gráfico de linha mostra a evolução do progresso ao longo do tempo, permitindo acompanhar o desenvolvimento do programa.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="framework-progress"]',
    content: 'Este gráfico exibe o progresso individual de cada framework de conformidade implementado no programa.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="status-chart"]',
    content: 'A distribuição dos status dos controles mostra quantos estão implementados, em progresso, não implementados, etc.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="maturity-card"]',
    content: 'O nível de maturidade indica o grau de implementação dos controles, variando de inicial até otimizado.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="entity-donut"]',
    content: 'Este gráfico de rosca mostra a distribuição de controles entre diferentes tipos de entidades (processos, ativos, dados, etc.).',
    disableScrolling: false,
  },
  {
    target: '[data-tour="status-by-entity"]',
    content: 'Visualize o status dos controles agrupados por tipo de entidade, facilitando identificar onde estão as lacunas de implementação.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="unique-entities"]',
    content: 'Este card mostra o total de entidades únicas que possuem controles vinculados no programa.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="entity-progress"]',
    content: 'A tabela de progresso detalha o avanço da implementação para cada tipo de entidade.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="entity-tabs"]',
    content: 'Use as abas para explorar detalhes dos controles vinculados a cada tipo de entidade. Cada aba contém uma tabela expansível com informações específicas.',
    disableScrolling: false,
  },
  {
    target: 'body',
    content: 'Tour concluído! Use este dashboard para monitorar o progresso do Programa de Privacidade e Proteção de Dados e identificar áreas que necessitam atenção.',
    disableScrolling: true,
    placement: 'center',
  },
];
